import { 
  RequestCaseEntityId, ReceiveCaseEntityId, FailRequestCaseEntityId, 
  ReceiveConnectedCasePermissions, RequestConnectedCasePermissions } from './action-types';
import { api } from './api';
import { ActionDispatching, Entity, CasePermissions } from 'app/types-shared';
import { errorSafe } from 'app/infrastructure/redux';
import { ValueChange } from 'app/infrastructure/ui/form/action-types';
import { columnName, tableName } from 'app/area/case/case-data-model';
import { getEntityContext, getCaseData } from 'app/infrastructure/ui/renderer/resolvers';
import { getChildEntitiesFromPath } from 'app/area/case/features/resolvers';
import { deepEquals } from 'app/infrastructure/js';
import clone from 'clone';
import sortBy from 'lodash/sortby';
import { sequence } from 'app/area/case/features/connected-cases/sequence';
import { getAllEntitiesAtPath } from 'app/infrastructure/store';
import { CaseState } from 'app/area/case/types-store';

const findChangedConnections = (updatedConnections: Entity[], originalConnections: Entity[]) => {
  const changed = [];

  for(const connection of updatedConnections) {
    const newSeq = sequence(connection);
    if(newSeq !== sequence(originalConnections.find(c => deepEquals(c.id, connection.id)))) {
      changed.push(connection);
    }
  }

  return changed;
};

const sortConnections = (connections: Entity[], direction: number) => {
  // Stable sort to preserve ordering beyond sequence.
  const sorted = sortBy(connections, c => sequence(c));

  if(direction > 0) {
    sorted.reverse();
  }

  return sorted;
};

/**
 * Works by swapping sequence with the next connection until the number of steps is reached.
 * Does nothing in the special case where the neighbour has the same sequence. 
 * Deal with this by providing a step count that is one greater than the number of identical sequence neighbours in the direction of change.
 */
export const changeSequence = (appState: any, uiEngineProps: any, steps: number) => {

  if(steps === 0) {
    return [];
  }

  const direction = Math.sign(steps);
  const absSteps = Math.abs(steps);
  const resolveContext = getEntityContext(appState, uiEngineProps);
  const originalConnections = getChildEntitiesFromPath(appState, uiEngineProps, 1, tableName.connectedCases);

  // Sort connections on sequence and reverse if we are moving downwards.
  const updatedConnections = clone(sortConnections(originalConnections, direction)) as Entity[];

  // Since we sorted we can work in one direction from now on on the array.
  const currentIndex = () => updatedConnections.findIndex(c => deepEquals(c.id, resolveContext.path));
  const startingIndex = currentIndex();

  if(startingIndex === updatedConnections.length - 1) {
    // Already at the edge. Do nothing.
    return [];
  }

  const safeIndex = (index: number) => updatedConnections.length > index && index >= 0;
  const row = (index: number) => safeIndex(index) && updatedConnections[index];

  const setSequence = (index: number, newSequence: number) => 
    updatedConnections[index][columnName[tableName.connectedCases].connectedSequence] = [{ value: newSequence }];
  
  let currIdx = currentIndex();

  while(safeIndex(currIdx + 1) && currIdx - startingIndex < absSteps) {
    const stepRow = row(currIdx + 1);
  
    // Swap with the next.
    const currentSequence = sequence(row(currIdx));
    let stepRowSequence = sequence(stepRow);

    // Tie-breaker. Clamp to 0 even if it means that two connections with the same 0 sequence cannot be swapped.
    if(currentSequence === stepRowSequence) {
      stepRowSequence = Math.max(0, stepRowSequence -= direction);
    }

    setSequence(currIdx, stepRowSequence);
    setSequence(currIdx + 1, currentSequence);
  
    // Move to avoid skipping index later.
    updatedConnections.splice(currIdx + 1, 0, updatedConnections.splice(currIdx, 1)[0]);

    currIdx = currentIndex();
  }

  return findChangedConnections(updatedConnections, originalConnections);
};

const updateChanged = (dispatch: any, changedConnections: Entity[]) => {
  for(const connection of changedConnections) {
    const newSeq = sequence(connection);
    dispatch(new ValueChange(connection.id, columnName[tableName.connectedCases].connectedSequence, [{ value: newSeq }]));
  }
};

export const connectedCaseActionCreators = {
     validateCaseId: (caseId: number): ActionDispatching<any, any> =>
      async (dispatch: any, getState: any) => {
        try {
          dispatch(new RequestCaseEntityId(caseId));
          const entityId = await api.caseEntityId(caseId);
          dispatch(new ReceiveCaseEntityId(caseId, entityId));
        } catch {
          dispatch(new FailRequestCaseEntityId(caseId));
        }
    },
    changeConnectionSequence: (uiEngineProps: any, steps: number): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        const appState = getState();
        const changed = changeSequence(appState, uiEngineProps, steps);
        updateChanged(dispatch, changed);
      }),
    getPermissions: (uiEngineProps: any): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        const appState = getState();
        const caseState = appState.caseArea as CaseState;

        const connectedCaseIds = getAllEntitiesAtPath(
            getCaseData(caseState, uiEngineProps), 
            [tableName.trans, tableName.connectionTypes, tableName.connectedCases]
          )
          .map(e => e.primaryKey.value);
        
        const ids = connectedCaseIds.filter(id =>
          (
            !caseState.stateForCaseIds ||
            !caseState.stateForCaseIds[id] ||
            (
              caseState.stateForCaseIds && 
              caseState.stateForCaseIds[id] && 
              !caseState.stateForCaseIds[id].fetchingPermissions &&
              !caseState.stateForCaseIds[id].permissions
            )
          )
        );

        if (!ids || ids.length === 0) {
          return;
        }

        dispatch(new RequestConnectedCasePermissions(ids));

        const [casePermissions, bowtieSupported] = await Promise.all([
          api.casePermissions(ids),
          api.bowtieSupported(ids)
        ]);
  
        const permissions = ids.reduce((result, id) => {
          const permission = casePermissions.find(p => p.caseId === id);
          const bowtie = bowtieSupported.find(p => p.caseId === id);
          return {
            ...result,
            [id]: {
              ...permission,
              bowtie: !!bowtie && bowtie.supported
            }
          };
        }, {} as {[id: number]: CasePermissions});
  
        dispatch(new ReceiveConnectedCasePermissions(permissions));
      })
  };
